'use client'

import { useTranslations } from 'next-intl'
import { Input, Textarea } from '@nextui-org/react'
import { Button } from '@/shared/ui/button/button'
import { useRef, useState } from 'react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'

const inputStyles = {
  input: 'bg-transparent',
  inputWrapper: 'bg-transparent hover:bg-default-100 rounded-md'
}

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY ?? ''

export const BuyForm = () => {
  const t = useTranslations('buy')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [cfToken, setCfToken] = useState('')
  const turnstileRef = useRef<TurnstileInstance>(null)
  const formRef = useRef<HTMLFormElement>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    setIsSuccess(false)
    setErrorMessage('')

    const formData = new FormData(e.currentTarget)
    const data = {
      type: 'buy' as const,
      name: formData.get('name') as string,
      phone: formData.get('phone') as string,
      email: formData.get('email') as string,
      message: `Бюджет: ${formData.get('budget') || 'Не указан'}\nТребования: ${formData.get('requirements') || 'Не указаны'}`,
      // Honeypot
      website: (formData.get('website') as string) || '',
      // Cloudflare Turnstile token
      cfToken
    }

    try {
      const response = await fetch('/api/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      })

      if (response.ok) {
        setIsSuccess(true)
        formRef.current?.reset()
        turnstileRef.current?.reset()
        setCfToken('')
      } else {
        const json = (await response.json().catch(() => ({}))) as { message?: string }
        setErrorMessage(json.message ?? 'Ошибка отправки заявки')
        turnstileRef.current?.reset()
        setCfToken('')
      }
    } catch {
      setErrorMessage('Ошибка отправки заявки')
      turnstileRef.current?.reset()
      setCfToken('')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="container py-6 md:py-12">
      <div className="mx-auto max-w-[600px]">
        <h2 className="mb-2 text-2xl md:text-3xl font-bold text-center">{t('title')}</h2>
        <p className="mb-6 md:mb-8 text-sm md:text-base text-muted-foreground text-center">
          {t('description')}
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} ref={formRef}>
          {/* Honeypot — скрытое поле, нормальный пользователь не заполняет */}
          <input
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ display: 'none' }}
          />
          <Input
            name="name"
            label={t('form.name')}
            variant="bordered"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="phone"
            label={t('form.phone')}
            type="tel"
            variant="bordered"
            placeholder="+7 (999) 999-99-99"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="email"
            label={t('form.email')}
            type="email"
            variant="bordered"
            classNames={inputStyles}
            isRequired
          />
          <Input
            name="budget"
            label={t('form.budget')}
            type="number"
            variant="bordered"
            placeholder="1000000"
            classNames={inputStyles}
          />
          <Textarea
            name="requirements"
            label={t('form.requirements')}
            variant="bordered"
            minRows={3}
            placeholder={t('form.requirementsPlaceholder')}
            classNames={inputStyles}
          />
          {/* Cloudflare Turnstile */}
          {TURNSTILE_SITE_KEY && (
            <Turnstile
              ref={turnstileRef}
              siteKey={TURNSTILE_SITE_KEY}
              onSuccess={(token) => setCfToken(token)}
              onExpire={() => setCfToken('')}
              onError={() => setCfToken('')}
              options={{ theme: 'light', size: 'flexible' }}
            />
          )}
          <Button
            type="submit"
            className="w-full h-11 bg-orange-500 hover:bg-orange-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Отправка...' : t('form.submit')}
          </Button>
          {isSuccess && (
            <p className="text-center text-green-600">Заявка успешно отправлена!</p>
          )}
          {errorMessage && (
            <p className="text-center text-red-600">{errorMessage}</p>
          )}
        </form>
      </div>
    </section>
  )
}
